import {
  ApeironEllipsisTypography,
  IconText,
  MayNull,
  NextImage
} from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import FirstPNG from '@public/icons/icon-1st.png'
import SecondPNG from '@public/icons/icon-2nd.png'
import ThirdPNG from '@public/icons/icon-3rd.png'
import ProfilePNG from '@public/icons/icon-user-profile.png'
import { GUILD_WAR_LEADERBOARD_NO_VALUE } from '@src/constants/battleDemo'
import {
  BattleDemoGuildPvPRank,
  BattleDemoRewardLite,
  BattleDemoSeason,
  BattleDemoSoloPvERank,
  BattleDemoSoloPvPRank
} from '@src/types/battleDemo'
import * as R from 'ramda'
import { FC, useMemo } from 'react'

const RankIcon = styled(NextImage)`
  height: 30px;
  width: 22px;
`

const RankContainer = styled(Box)`
  height: auto;
  text-align: center;
  width: 22px;
`

const NameWithIcon = styled(IconText)`
  gap: 10px;
  width: 100%;
`

const Address = styled(ApeironEllipsisTypography)`
  text-align: center;
  font-size: 14px;
  font-weight: 300;
  line-height: 14px;
  width: 100%;
`

const RewardContainer = styled(Box)`
  display: flex;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    justify-content: center;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    justify-content: center;
  }
`

const RewardName = styled(ApeironEllipsisTypography)`
  font-size: 14px;
  font-weight: 300;
  line-height: 14px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    text-align: right;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    text-align: center;
  }
`

export const BattleDemoCellContent = styled(Box)`
  align-items: center;
  color: white;
  display: flex;
  gap: 6px;
  height: 100%;
  justify-content: flex-start;
  width: 100%;
`

export const BattleDemoCellCenterContent = styled(BattleDemoCellContent)`
  justify-content: center;
`

export const BattleDemoRanking: FC<ShareRankCellProps> = props => {
  const { record, className } = props

  if (record?.rank === undefined) return <></>
  switch (record?.rank) {
    case 1:
      return <RankIcon className={className} src={FirstPNG} />

    case 2:
      return <RankIcon className={className} src={SecondPNG} />

    case 3:
      return <RankIcon className={className} src={ThirdPNG} />

    case GUILD_WAR_LEADERBOARD_NO_VALUE:
      return <RankContainer className={className}>---</RankContainer>

    default:
      return <RankContainer className={className}>{record?.rank}</RankContainer>
  }
}

export const BattleDemoNameCell: FC<ShareRankCellProps> = props => {
  const { record, className } = props

  if (R.isNil(record?.rank)) return <></>

  let icon = ''

  let name = ''

  if (R.has('user', record)) {
    const entity = record

    icon = ProfilePNG.src
    name = entity.user.name
  } else {
    const entity = record as BattleDemoGuildPvPRank

    icon = entity.guild.icon
    name = entity.guild.name
  }

  return (
    <BattleDemoCellContent className={className}>
      <NameWithIcon startSrc={icon} text={name} />
    </BattleDemoCellContent>
  )
}

export const BattleDemoAddressCell: FC<ShareRankCellProps> = props => {
  const { record, className } = props

  return (
    <BattleDemoCellContent className={className}>
      <Address>{R.pathOr('', ['user', 'walletAddress'], record)}</Address>
    </BattleDemoCellContent>
  )
}

const BattleDemoRewardCellContent = styled(BattleDemoCellContent)`
  flex-direction: column;
  justify-content: center;
`

export const BattleDemoRewardCell: FC<RewardCellProps> = (
  props: RewardCellProps
) => {
  const { record, season, className } = props

  const rank = R.propOr(-1, 'rank', record) as number

  const rewardGroups = useMemo(
    () => season?.rewardGroups || [],
    [season?.rewardGroups]
  )

  const basicReward = useMemo(() => {
    return R.find(group => {
      return rank >= group.rankMin && rank <= group.rankMax
    }, rewardGroups)
  }, [rank, rewardGroups])

  return (
    <BattleDemoRewardCellContent className={className}>
      {R.map((reward: BattleDemoRewardLite) => {
        const { name, value } = reward

        const fullName = value > 1 ? `x${value} ${name}` : name

        return (
          <RewardContainer key={name}>
            <RewardName>{fullName}</RewardName>
          </RewardContainer>
        )
      }, basicReward?.rewardsBasic || [])}
    </BattleDemoRewardCellContent>
  )
}

type ShareRankCellProps = {
  className?: string
  record: MayNull<
    BattleDemoSoloPvERank | BattleDemoSoloPvPRank | BattleDemoGuildPvPRank
  >
}

type RewardCellProps = ShareRankCellProps & {
  season: MayNull<BattleDemoSeason>
}
